// Free level
type FeatureCount = null | number;

export const FEATURE_FLAGS_IDS = {
  cardsSearch: 'featureFlags/1',
  articlesView: 'featureFlags/2',
  statsView: 'featureFlags/3',
  locationReminders: 'featureFlags/4',
  dayReminders: 'featureFlags/5',
  phq9: 'featureFlags/6',
  gad7: 'featureFlags/7',
  meditations: 'featureFlags/8',
  worryTrees: 'featureFlags/9',
  crisisContact: 'featureFlags/10',
  downwardArrows: 'featureFlags/11',
  behaviorExperiments: 'featureFlags/12',
  exposures: 'featureFlags/13',
  exposureSituations: 'featureFlags/14',
  exposureSituationReactions: 'featureFlags/15',
  aiImages: 'featureFlags/16',
  videoView: 'featureFlags/17',
  login: 'featureFlags/18',
  // E27 — the synthetic target for the force-upgrade gate's usage emit. The gate is about the BUILD,
  // not about a document, so it takes a stable id the same way `login` does (the shipped convention
  // for a usage event with no row behind it).
  upgradeGate: 'featureFlags/19',
  /**
   * M7 (E03, 2026-09-13) — three more synthetic targets, for the same reason `login` and
   * `upgradeGate` are synthetic: the act has no document behind it. A sign-up, a completed password
   * reset and a consent save are things a PERSON did, not things done to a row, and the board asks
   * for `focusType: feature` with a domain noun as the focus — `feature/signup`,
   * `feature/passwordReset`, `feature/privacy`.
   *
   * THE PRIVACY ONE REPLACES A SHIPPED EMIT (founder Q14). Today `PrivacySettings` save writes
   * `Updated` (5) with `_to` = the person's OWN `users/<key>` and the whole settings object stuffed
   * into `extras` — a row the portal's activity list renders as `featureFlags.users/53`, i.e. as
   * nothing, and a payload EXTRAS-1 forbids. 26 such rows on dev; no counter anywhere filters type
   * 5 against a `users/` focus (verified 2026-09-13 against the seven converters' shadow-edge scans,
   * which all filter by the ENTITY id, and against the two portal counters, which filter by
   * `entityPrefix`).
   */
  signUp: 'featureFlags/20',
  passwordReset: 'featureFlags/21',
  privacy: 'featureFlags/22',
  /**
   * E08 (2026-09-21) — the paywall opened (`PaywallViewed`, board `feature` / `paywall`): a
   * synthetic target for the same reason as `login` — nothing but a screen behind it. The
   * purchase and restore emits target `subscriptions/premium` (the board's `subscription` /
   * `premium`; the E-era split names the collection), not a feature flag.
   */
  paywall: 'featureFlags/23',
};

/**
 * THE E-ERA USAGE FOCUS VOCABULARY (founder ruling Q6, 2026-09-12: *"Yes, usage focus vocabulary"*
 * — E13/E14/E15 build item 5).
 *
 * A usage event says WHAT IT IS ABOUT with `focusType` + `focus`, the E-era split of the legacy
 * edge's `_to` (OVERLAY §1's FHIR mapping: `thoughts/109` → `thoughtTest` + `109`). Every feature
 * flag is one domain noun, so the whole vocabulary is this map read in one direction or the other:
 *
 *   `featureFlags/8`  ⇄  `{ focusType: 'feature', focus: 'meditations' }`
 *   `featureFlags/16` ⇄  `{ focusType: 'feature', focus: 'aiImages' }`
 *
 * WHY IT LIVES HERE and not next to `focusOf()` in the API. The map is needed in BOTH directions
 * by different packages — the server translates a legacy id into the noun before appending to
 * `usage_log`; the client translates the noun back into the `_to` the shipped `createUsageEvents`
 * wire still carries (build item 8: the telemetry does not move until E04's own sync slice lands).
 * Two tables would be two things to keep in step, and `FEATURE_FLAGS_IDS` — which both already
 * import — is the one place the pairing is defined.
 *
 * FHIR-3 is the reason it exists at all: a bare Arango-shaped id (`featureFlags/16`) is not a
 * domain noun and is unreadable in a log a year later. The four names the wellness boards use —
 * `meditations`, `crisisContact`, `articlesView`, `videoView` — fall out of the same map as
 * everything else, so there is no wellness special case.
 */
export const USAGE_FEATURE_FOCUS_TYPE = 'feature';

/**
 * E30 · the focus type of a usage event that is about a PORTAL BUILD rather than about a row or a
 * feature — `{ focusType: 'portalBuild', focus: '9f3c2a1' }`.
 *
 * It lives beside `USAGE_FEATURE_FOCUS_TYPE` because it is the same vocabulary, and it is NOT a
 * `FEATURE_FLAGS_IDS` entry on purpose (registry `A-usage-ordinal`): `login`, `upgradeGate` and
 * friends are synthetic targets standing in for a feature, whereas the build id is itself the
 * domain noun the row is about. `portalBuildFocusTarget('9f3c2a1')` is the `_to` the shipped
 * `createUsageEvents` wire still carries, and `focusOf()` splits it straight back into the pair.
 */
export const USAGE_PORTAL_BUILD_FOCUS_TYPE = 'portalBuild';

/** `9f3c2a1` → `portalBuild/9f3c2a1` — the legacy edge target for an E30 usage emit. */
export const portalBuildFocusTarget = (buildId: string): string =>
  `${USAGE_PORTAL_BUILD_FOCUS_TYPE}/${buildId}`;

const FEATURE_NAME_BY_FLAG_ID: Record<string, string> = Object.entries(
  FEATURE_FLAGS_IDS,
).reduce<Record<string, string>>((acc, [name, id]) => {
  acc[id] = name;
  return acc;
}, {});

/** `featureFlags/8` → `meditations`; `null` for anything that is not a feature-flag id. */
export const featureNameOfFlagId = (flagId: string): string | null =>
  FEATURE_NAME_BY_FLAG_ID[flagId] ?? null;

/** `meditations` → `featureFlags/8`; `null` for a noun no flag carries. */
export const featureFlagIdOfName = (name: string): string | null =>
  (FEATURE_FLAGS_IDS as Record<string, string | undefined>)[name] ?? null;

export const featureFlags = {
  cardsSearch: {
    name: 'cardsSearch',
    count: null as FeatureCount, // unlimited
  },
  // Learn content stays BUNDLED — founder G1 2026-08-29 (E15 `content-to-db`): *"not convinced a
  // server-side version is better (mobile, offline-first)"*. The `// TODO: Articles are very
  // likely to be moved to DB` that stood here was deleted on 2026-09-12 (build item 7) so the
  // question stops re-opening itself; a DB/CMS remains a later candidate, not a plan.
  articlesView: {
    name: 'articlesView',
    count: null as FeatureCount, // unlimited
  },
  statsView: {
    name: 'statsView',
    count: 1 as FeatureCount,
  },
  locationReminders: {
    name: 'locationReminders',
    count: 2 as FeatureCount,
  },
  dayReminders: {
    name: 'dayReminders',
    count: 9 as FeatureCount,
  },
  phq9: {
    name: 'phq9',
    count: 14 as FeatureCount, // To be used as limit in days since last test
  },
  gad7: {
    name: 'gad7',
    count: 14 as FeatureCount, // To be used as limit in days since last test
  },
  meditations: {
    name: 'meditations',
    count: 3 as FeatureCount, // To be used as limit in days since last play
  },
  worryTrees: {
    name: 'worryTrees',
    count: 1 as FeatureCount, // To be used as limit in days since last play
  },
  downwardArrows: {
    name: 'downwardArrows',
    count: 2 as FeatureCount, // To be used as limit in days since last play
  },
  crisisContact: {
    name: 'crisisContact',
    count: null as FeatureCount, // unlimited
  },
  behaviorExperiments: {
    name: 'behaviorExperiments',
    count: 3 as FeatureCount, // To be used as limit in days since last created
  },
  exposures: {
    name: 'exposures',
    count: 3 as FeatureCount, // To be used as limit in days since last created (1 exposure per 3 days)
  },
  exposureSituations: {
    name: 'exposureSituations',
    count: null, // No limit currently
  },
  exposureSituationReactions: {
    name: 'exposureSituationReactions',
    count: null, // No limit currently
  },
  aiImages: {
    name: 'aiImages',
    count: 3 as FeatureCount, // To be used as free limit within 24 hours
  },
  videoView: {
    name: 'videoView',
    count: null,
  },
  login: {
    name: 'login',
    count: null,
  },
};

export const premiumFeatureFlags: typeof featureFlags = Object.entries(
  featureFlags,
).reduce((acc, entry) => {
  // @ts-ignore
  acc[entry[0]] = { ...entry[1], count: null };
  return acc;
}, {} as typeof featureFlags);
